import React from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { FaCompass } from 'react-icons/fa';
import { AssessmentWizard } from './components/AssessmentWizard';
import { AssessmentSection } from './components/AssessmentSection';
import { AssessmentResults } from './components/AssessmentResults';
import { Footer } from '../components/shared/Footer';
import { useAssessmentForm } from '../hooks/useAssessmentForm';
import formSchema from '../data/formSchema.json';

const AIAssessmentForm = () => {
  const {
    currentSection,
    formData,
    errors,
    loading,
    submitError,
    results,
    totalSections,
    updateField,
    nextSection,
    prevSection,
    submitForm,
    resetForm,
    progress,
    isLastSection,
    canGoNext,
    canGoPrev,
  } = useAssessmentForm(formSchema);

  // If results are available, show results page
  if (results) {
    return (
      <AssessmentResults
        scores={results.scores}
        currentStage={formData.current_state?.stage_self_assessment}
        targetStage={formData.goals_and_priorities?.target_stage}
        onReset={resetForm}
        formData={formData}
        recommendations={results.recommendations}
      />
    );
  }

  const currentSectionData = formSchema.sections[currentSection];

  return (
    <div className="min-h-screen bg-gray-900 text-gray-100">
      {/* Header */}
      <div className="bg-gradient-to-r from-teal-600/20 to-blue-600/20 border-b border-teal-500/30 pt-24 pb-12">
        <div className="max-w-5xl mx-auto px-6">
          <motion.div
            initial={{ opacity: 0, y: -20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="text-center"
          >
            <div className="flex items-center justify-center mb-4">
              <FaCompass className="text-4xl text-teal-400 mr-3" />
              <h1 className="text-4xl md:text-5xl font-bold bg-clip-text text-transparent bg-gradient-to-r from-teal-400 to-blue-400">
                AI Strategy & Readiness Assessment
              </h1>
            </div>
            <p className="text-lg text-gray-300 max-w-3xl mx-auto">
              Complete this comprehensive assessment to evaluate your organization's AI maturity 
              and receive personalized recommendations for your AI transformation journey.
            </p>
            <div className="mt-4 text-sm text-gray-400">
              <span className="inline-flex items-center gap-2">
                <span className="w-2 h-2 bg-teal-400 rounded-full"></span>
                Estimated time: {formSchema.form_metadata.estimated_time_minutes} minutes
              </span>
            </div>
          </motion.div>
        </div>
      </div>

      {/* Wizard Navigation */}
      <AssessmentWizard
        currentSection={currentSection}
        totalSections={totalSections}
        progress={progress}
        onNext={nextSection}
        onPrev={prevSection}
        onSubmit={submitForm}
        canGoNext={canGoNext}
        canGoPrev={canGoPrev}
        isLastSection={isLastSection}
        loading={loading}
        sections={formSchema.sections}
      />

      {/* Form Content */}
      <div className="max-w-5xl mx-auto px-6 py-12">
        {submitError && (
          <motion.div
            initial={{ opacity: 0, y: -10 }}
            animate={{ opacity: 1, y: 0 }}
            className="mb-8 p-4 bg-red-500/20 border border-red-500 rounded-lg text-red-400"
          >
            <p className="font-semibold">Error submitting assessment:</p>
            <p>{submitError}</p>
          </motion.div>
        )}

        <AnimatePresence mode="wait">
          <AssessmentSection
            key={currentSection}
            section={currentSectionData}
            formData={formData}
            updateField={updateField}
            errors={errors}
          />
        </AnimatePresence>

        {/* Helper Text */}
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.5 }}
          className="mt-12 p-6 bg-gray-800/50 border border-gray-700 rounded-lg"
        >
          <p className="text-sm text-gray-400 text-center">
            <span className="text-teal-400 font-semibold">Note:</span> All information is confidential 
            and will only be used to provide you with personalized recommendations. 
            Fields marked with <span className="text-red-400">*</span> are required.
          </p>
        </motion.div>
      </div>

      <Footer />
    </div>
  );
};

export default AIAssessmentForm;

